/**
 * The Knowledge screen's shapes and pure helpers (docs/CONTRACT-company-knowledge-import.md sections 3 to 6): what the
 * company knowledge admin routes answer, how a textarea becomes a list, how a loaded project row becomes the row the
 * PUT takes, the plain words for each refusal the routes send, and the labels the screen shows. Pure, so
 * apps/admin/test/knowledge.test.ts proves them without a browser.
 */

export const LIFECYCLES = ['active', 'negotiating', 'completed', 'removal_requested', 'removed'] as const;
export type Lifecycle = (typeof LIFECYCLES)[number];
/** The lifecycles an admin may set from the screen: `removed` is set only by an approved removal. */
export const SETTABLE_LIFECYCLES = ['active', 'negotiating', 'completed', 'removal_requested'] as const;

export interface ProjectDates {
  createdAt: string;
  activeSince: string | null;
  negotiatingSince: string | null;
  completedAt: string | null;
  removalRequestedAt: string | null;
  removedAt: string | null;
}

export interface DeletionClock {
  startedAt: string;
  deleteBy: string;
  daysLeft: number;
  overdue: boolean;
  /** Projects of the same party now negotiating: the strategy's exception, shown, never applied by itself. */
  heldBy: string[];
}

/** A project row as GET /v1/admin/knowledge/projects answers it. */
export interface ProjectView {
  id: string;
  folderPaths: string[];
  directClient: string;
  endUser: string | null;
  endUserUnknown: boolean;
  pools: string[];
  namedReaders: string[];
  restricted: boolean;
  lifecycle: Lifecycle;
  dates: ProjectDates;
  clock: DeletionClock | null;
  notes: string;
}

/** A project row as PUT and PATCH take it. */
export interface ProjectInput {
  id: string;
  folderPaths: string[];
  directClient: string;
  endUser: string | null;
  pools: string[];
  namedReaders: string[];
  restricted: boolean;
  lifecycle: Lifecycle;
  notes?: string;
}

export interface FolderRow {
  path: string;
  scope: 'internal' | 'none';
  notes?: string;
}

export interface ProjectsView {
  revision: number;
  projects: ProjectView[];
  folders: FolderRow[];
  exclusions: string[];
  limits: Record<string, number>;
}

export interface PoolRow {
  id: string;
  name: string;
  emails: string[];
  entraGroupId: string | null;
}

export interface PoolsView {
  revision: number;
  pools: PoolRow[];
  principalsPool: string;
  limits: Record<string, number>;
}

export interface DocumentRow {
  documentId: string;
  path: string;
  sha256: string;
  textKey?: string;
  modifiedAt: string;
  sizeBytes: number;
  title: string;
  mimeType: string;
  sectionCount: number;
  textChars: number;
  dlpHits: number;
  droppedSections: number;
  importedAt: string;
}

export interface SourcesPage {
  scope: unknown;
  partition: string;
  total: number;
  page: number;
  pageSize: number;
  pages: number;
  documents: DocumentRow[];
}

export interface RemovalRow {
  id: string;
  projectId: string;
  reason: string;
  requestedBy: string;
  at: number;
}

export interface ReportProject {
  projectId: string;
  role: 'directClient' | 'endUser' | 'both';
  directClient: string;
  endUser: string | null;
  lifecycle: Lifecycle;
  dates: ProjectDates;
  clock: DeletionClock | null;
  partition: { state: string; removedAt?: string };
  counts: { documents: number; sections: number; textChars: number; dlpHits: number; droppedSections: number; memoryLines: number; retrievalLogEntries: number };
  documents: string[];
  documentsTotal: number;
  documentsTruncated: boolean;
}

export interface ReportView {
  party: string;
  generatedAt: string;
  projects: ReportProject[];
  survives: string[];
  notCovered: string[];
}

export interface KnowledgeStatus {
  flag: { stored: boolean; enabled: boolean; percent: number | null; roles: string[] };
  storage: 'durable' | 'local';
  projects: number | null;
  pools: number | null;
  /** Project partitions holding documents with no row on the list; null when storage could not be listed. */
  orphans: Array<{ partition: string; projectId: string }> | null;
  limits: Record<string, number>;
}

/** A scope as the sources routes take it on the query string: `internal`, `none` or `project:<id>`. */
export type ScopeText = 'internal' | 'none' | `project:${string}`;

/** A textarea of one entry per line: trimmed, blanks dropped, each kept once (first spelling wins, case kept). */
export function parseLines(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const key = line.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(line);
  }
  return out;
}

/** A field of ids or e-mails separated by commas, semicolons or line breaks: trimmed, lower-cased, blanks dropped, each once. */
export function parseList(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[,;\r\n]+/)) {
    const item = raw.trim().toLowerCase();
    if (item && !out.includes(item)) out.push(item);
  }
  return out;
}

/** The row PUT and PATCH take, from a row GET answered: the view-only fields (`endUserUnknown`, `clock`, `dates`) go. */
export function toProjectInput(row: ProjectView): ProjectInput {
  return {
    id: row.id,
    folderPaths: [...row.folderPaths],
    directClient: row.directClient,
    endUser: row.endUser,
    pools: [...row.pools],
    namedReaders: [...row.namedReaders],
    restricted: row.restricted,
    lifecycle: row.lifecycle,
    ...(row.notes ? { notes: row.notes } : {}),
  };
}

/** The body of PUT /v1/admin/knowledge/projects: the whole list at the revision the screen loaded. */
export function projectsPutBody(list: ProjectsView, projects: ProjectInput[], folders: FolderRow[] = list.folders, exclusions: string[] = list.exclusions): {
  projects: ProjectInput[];
  folders: FolderRow[];
  exclusions: string[];
  expectedRevision: number;
} {
  return {
    projects,
    folders: folders.map((folder) => ({ path: folder.path, scope: folder.scope, ...(folder.notes ? { notes: folder.notes } : {}) })),
    exclusions: [...exclusions],
    expectedRevision: list.revision,
  };
}

/** The folder rows of one scope, from a textarea of paths. */
export function foldersFromLines(text: string, scope: FolderRow['scope']): FolderRow[] {
  return parseLines(text).map((path) => ({ path, scope }));
}

/** A textarea of the folder paths of one scope. */
export function folderLines(folders: readonly FolderRow[], scope: FolderRow['scope']): string {
  return folders.filter((folder) => folder.scope === scope).map((folder) => folder.path).join('\n');
}

/** The scope text for the sources routes. */
export function scopeTextFor(selection: string): ScopeText {
  return selection === 'internal' || selection === 'none' ? selection : `project:${selection}`;
}

/** The clock in words: nothing, the days left and the date, overdue, and who holds it. */
export function clockLabel(clock: DeletionClock | null): string {
  if (!clock) return '';
  const day = clock.deleteBy.slice(0, 10);
  const head = clock.overdue ? `delete overdue since ${day}` : `delete by ${day} (${clock.daysLeft} day${clock.daysLeft === 1 ? '' : 's'} left)`;
  return clock.heldBy.length ? `${head}; the same party is negotiating ${clock.heldBy.join(', ')}` : head;
}

/** Bytes as people read them. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** What a refused call looks like to the screen (lib/api.ts ApiError, duck-typed so this module stays pure). */
export interface RefusalLike {
  message: string;
  status?: number;
  body?: Record<string, unknown>;
}

/**
 * Plain words for a refusal the knowledge routes send (the contract's codes); the server's own message for anything
 * else, and a generic line when there is none. Never a stack, never a raw code alone.
 */
export function describeKnowledgeError(error: unknown): string {
  const refusal = error as RefusalLike | undefined;
  const code = typeof refusal?.body?.error === 'string' ? refusal.body.error : undefined;
  const serverMessage = typeof refusal?.body?.message === 'string' ? refusal.body.message : undefined;
  const field = typeof refusal?.body?.field === 'string' ? refusal.body.field : undefined;
  switch (code) {
    case 'knowledge_projects_changed':
    case 'knowledge_pools_changed':
      return 'Another admin saved a change after you loaded this screen, so nothing was saved. The list has been reloaded; make your change again.';
    case 'knowledge_project_has_documents':
      return serverMessage ?? 'That project still holds documents, so it cannot be dropped from the list. Request its removal instead, or delete its sources first.';
    case 'knowledge_storage_unavailable':
      return 'The library could not be read or written just now. Nothing was changed; try again in a minute.';
    case 'not_pending':
      return serverMessage ?? 'That removal is no longer waiting for a decision.';
    case 'project_unknown':
      return serverMessage ?? 'That project is not on the list.';
    case 'project_removed':
      return serverMessage ?? 'That project is being removed, or was removed.';
    case 'invalid_request':
      return field ? `${serverMessage ?? 'The request was refused'} (field: ${field}).` : serverMessage ?? 'The request was refused.';
    case 'forbidden':
      return 'Your account may view this screen but not change it (USER-ECOE-IT-ADMIN is required).';
    case 'unauthenticated':
      return 'Your sign-in expired. Reload the page to sign in again.';
    default:
      if (refusal?.status === 403) return 'Your account may view this screen but not change it (USER-ECOE-IT-ADMIN is required).';
      if (refusal?.status === 404 && !code) return 'This gyro-svc does not have the company knowledge library yet (it ships with the next release).';
      return serverMessage ?? (typeof refusal?.message === 'string' && refusal.message ? refusal.message : 'The request failed.');
  }
}

/** Whether the server says the library routes are absent (an older gyro-svc): the screen then shows one line instead of its cards. */
export function isLibraryAbsent(error: unknown): boolean {
  const refusal = error as RefusalLike | undefined;
  return refusal?.status === 404 && typeof refusal.body?.error !== 'string';
}
